import { Fragment, useEffect } from "react";
import Typography from "@material-ui/core/Typography";

import { InputSelectRequired } from "../../shared/components/FormElements/InputSelectRequired";
import { InputPrefixOutlinedFloat } from "../../shared/components/FormElements/InputPrefixOutlinedFloat";
import { removeFormStateProperty } from "../../shared/util/removeFormStateProperties";
import { InputAutocompleteArrayRequired } from "../../shared/components/FormElements/InputAutocompleteArrayRequired";
import { InputPrefixOutlinedInt } from "../../shared/components/FormElements/InputPrefixOutlinedInt";
import { updateFormValidity } from "../../shared/util/updateFormValidity";
import {
  VALIDATOR_MIN,
  VALIDATOR_FLOAT,
  VALIDATOR_INT,
  VALIDATOR_REQUIRE,
} from "../../shared/util/validators";
import { TAFIFF_MODALITIES, TARIFF_CLASS } from "../../shared/data/static";
import { FormHookDispState, FeeData } from "../../shared/data/types";

import { ProposalStepOneEditStyles } from "../../proposal/pages/ProposalStepOneEditStyles";

interface InputsSimFormEnergyBillDataProps {
  inputHandler: (
    id: string,
    value: string | boolean | File,
    isValid: boolean,
    label: string
  ) => void;
  formState: FormHookDispState;
  loadedFees: FeeData[];
  isAGroup: boolean;
}

export const InputsSimFormEnergyBillData = (
  props: InputsSimFormEnergyBillDataProps
) => {
  const { inputHandler, formState, loadedFees, isAGroup } = props;
  const classes = ProposalStepOneEditStyles();
  const powerDistCompanyOpts =
    loadedFees?.map((tariff) => {
      return tariff.powerDistCompany;
    }) || [];

  useEffect(() => {
    if (formState?.inputs?.tariffModality?.value === TAFIFF_MODALITIES[0]) {
      removeFormStateProperty(formState, "classification");
      removeFormStateProperty(formState, "energyBillPricePeak");
      removeFormStateProperty(formState, "demand");
      removeFormStateProperty(formState, "demandPeak");

      formState.isValid = updateFormValidity(formState);
    }
  }, [formState?.inputs?.tariffModality?.value]);

  useEffect(() => {
    if (formState?.inputs?.classification?.value === "Verde") {
      removeFormStateProperty(formState, "demandPeak");

      formState.isValid = updateFormValidity(formState);
    }
  }, [formState?.inputs?.classification?.value]);

  return (
    <Fragment>
      {/* <Typography
        style={{ color: colors.light }}
        className={classes.titleSummaryze}
        variant="h6"
      >
        Conta de Energia
      </Typography> */}
      <div className="form-inputs-grid-3fr-responsive">
        <InputSelectRequired
          options={TAFIFF_MODALITIES}
          id="tariffModality"
          label="Modalidade Tarifária"
          variant="outlined"
          onInput={inputHandler}
          initialValue={TAFIFF_MODALITIES[0]}
          forceError={formState?.inputs?.tariffModality?.forceError}
        />
        <InputSelectRequired
          id="ucType"
          label="Propriedade"
          variant="outlined"
          onInput={inputHandler}
          options={["Residencial", "Comercial", "Industrial"]}
          initialValue="Residencial"
          updateInitValue
          forceError={formState.inputs?.ucType?.forceError}
        />
        <InputAutocompleteArrayRequired
          options={powerDistCompanyOpts}
          id="powerDistCompany"
          label="Concessionária"
          variant="outlined"
          onInput={inputHandler}
          initialValue=""
          helperText="Selecione a concessionária da sua propriedade."
          forceError={formState?.inputs?.powerDistCompany?.forceError}
          reinitialize
        />
      </div>
      {!isAGroup && (
        <InputPrefixOutlinedFloat
          id="energyBillPrice"
          type="text"
          label="Valor da Conta de Energia"
          variant="outlined"
          prefix="R$"
          helperText="Insira o valor da sua fatura de energia em reais (mínimo R$ 10,00)."
          validators={[VALIDATOR_MIN(10), VALIDATOR_REQUIRE()]}
          initialValue={""}
          initialValid={false}
          onInput={inputHandler}
          forceError={formState?.inputs?.energyBillPrice?.forceError}
          updateInitValue
        />
      )}
      {isAGroup && (
        <Fragment>
          <InputSelectRequired
            id="classification"
            label="Classificação Horo-Sazonal"
            onInput={inputHandler}
            options={TARIFF_CLASS}
            initialValue=""
            variant="outlined"
            forceError={formState?.inputs?.classification?.forceError}
            updateInitValue
          />
          <div className="form-inputs-grid-1fr-1fr form-inputs-grid-responsive">
            <InputPrefixOutlinedFloat
              id="energyBillPrice"
              type="text"
              label="Fatura de Energia Fora Ponta"
              variant="outlined"
              prefix="R$"
              helperText="Insira o valor da sua fatura de energia (fora ponta) em reais (mínimo R$ 10,00)."
              initialValid={false}
              validators={[VALIDATOR_MIN(10), VALIDATOR_REQUIRE()]}
              initialValue=""
              onInput={inputHandler}
              forceError={formState?.inputs?.energyBillPrice?.forceError}
              updateInitValue
            />
            <InputPrefixOutlinedFloat
              id="energyBillPricePeak"
              type="text"
              label="Fatura de Energia Ponta"
              variant="outlined"
              prefix="R$"
              helperText="Insira o valor da sua fatura de energia (ponta) em reais (mínimo R$ 10,00)."
              initialValid={false}
              validators={[VALIDATOR_MIN(10), VALIDATOR_REQUIRE()]}
              initialValue=""
              onInput={inputHandler}
              forceError={formState?.inputs?.energyBillPricePeak?.forceError}
              updateInitValue
            />
          </div>
          {formState?.inputs?.classification?.value === "Azul" && (
            <div className="form-inputs-grid-1fr-1fr form-inputs-grid-responsive">
              <InputPrefixOutlinedInt
                id="demand"
                type="text"
                label="Demanda Fora Ponta"
                variant="outlined"
                prefix="kW"
                helperText="Insira o valor da demanda contratada fora ponta em kW (mínimo 30kW)."
                validators={[VALIDATOR_MIN(30), VALIDATOR_INT()]}
                initialValue=""
                initialValid={true}
                onInput={inputHandler}
                forceError={formState?.inputs?.demand?.forceError}
                updateInitValue
              />
              <InputPrefixOutlinedInt
                id="demandPeak"
                type="text"
                label="Demanda Ponta"
                variant="outlined"
                prefix="kW"
                helperText="Insira o valor da demanda contratada ponta em kW."
                validators={[VALIDATOR_MIN(1), VALIDATOR_INT()]}
                initialValue=""
                initialValid={true}
                onInput={inputHandler}
                forceError={formState?.inputs?.demandPeak?.forceError}
                updateInitValue
              />
            </div>
          )}
          {formState?.inputs?.classification?.value === "Verde" && (
            <InputPrefixOutlinedInt
              id="demand"
              type="text"
              label="Demanda"
              variant="outlined"
              prefix="kW"
              helperText="Insira o valor da demanda contratada em kW (mínimo 30kW)."
              validators={[VALIDATOR_MIN(30), VALIDATOR_INT()]}
              initialValue=""
              initialValid={true}
              onInput={inputHandler}
              forceError={formState?.inputs?.demand?.forceError}
              updateInitValue
            />
          )}
        </Fragment>
      )}
    </Fragment>
  );
};
